import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Container,
  Alert,
  Modal,
  InputGroup,
  Badge,
} from "react-bootstrap";
import {
  Phone,
  Smartphone,
  Send,
  CheckCircle,
  Shield,
  CreditCard,
  RefreshCw,
  AlertCircle,
  Eye,
  FileText,
  DollarSign,
  Zap,
} from "react-feather";
import Swal from "sweetalert2";

import FinoDmtService from "../../../services/FinoDmtService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import SubscriptionDialog from "../../../components/Dialogs/SubscriptionDialog";
import { useSubscriptionCheck } from "../../../hooks/useSubscriptionCheck";
import ProfileService from "../../../services/ProfileService";
import { Info } from "react-bootstrap-icons";

const FinoMoneyTransfer = () => {
  const navigate = useNavigate();

  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [message, setMessage] = useState(null);
  const [otp, setOtp] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);

  // Account opening modal states
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showAccountRevisionModal, setShowAccountRevisionModal] =
    useState(false);
  const [accountOpeningUrl, setAccountOpeningUrl] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [revisionResponse, setRevisionResponse] = useState("");

  // Onboarding modal state
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [onboardMessage, setOnboardMessage] = useState("");

  const { premiumStatus, showDialog, setShowDialog, checkSubscriptionStatus } =
    useSubscriptionCheck();

  // Onboarding status from state (and localStorage)
  const [merchantStatus, setMerchantStatus] = useState(null);
  const [finoEkycStatus, setFinoEkycStatus] = useState(null);
  const [isOnboarded, setIsOnboarded] = useState(false);

  // Helper: check localStorage first, then decide whether to call API
  const checkLocalStorageOnboarding = () => {
    const storedMerchant = localStorage.getItem("merchant_status");
    const storedEkyc = localStorage.getItem("fino_ekyc_status");
    if (storedMerchant === "APPROVED" && storedEkyc === "VERIFIED") {
      setIsOnboarded(true);
      return true;
    }
    return false;
  };

  // Fetch merchant onboarding status from API (only if not already onboarded in localStorage)
  const fetchMerchantOnboardingStatus = async () => {
    // If localStorage already shows approved & verified, skip API call
    if (checkLocalStorageOnboarding()) {
      return;
    }

    try {
      const result = await FinoDmtService.merchantOnboardStatus();
      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;
      if (res.status === "SUCCESS") {
        const respData = res.data;
        const mStatus = respData?.merchant_status;
        const ekycStatus = respData?.fino_ekyc_status;

        // Store in state
        setMerchantStatus(mStatus);
        setFinoEkycStatus(ekycStatus);

        // Store in localStorage
        localStorage.setItem("merchant_status", mStatus);
        localStorage.setItem("fino_ekyc_status", ekycStatus);

        // Check if fully onboarded
        if (mStatus === "APPROVED" && ekycStatus === "VERIFIED") {
          setIsOnboarded(true);
        } else {
          setIsOnboarded(false);
          // Prepare message for modal
          let msg = "";
          if (mStatus !== "APPROVED") {
            msg += "Merchant onboarding is not approved. ";
          }
          if (ekycStatus !== "VERIFIED") {
            msg += "FINO e-KYC is not verified. ";
          }
          setOnboardMessage(msg);
          setShowOnboardModal(true);
        }
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || error.message || "Network error";
      Swal.fire("Oops!", errorMsg, "error");
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        await checkSubscriptionStatus();
        // Check onboarding status (uses localStorage first)
        await fetchMerchantOnboardingStatus();
        await fetchWalletBalance();
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  // Function to fetch wallet balance
  const fetchWalletBalance = async () => {
    try {
      setLoadingBalance(true);
      const result = await ProfileService.fetchBalance();
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }
      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        const bal = responseData?.data?.main_wallet;
        setWalletBalance(bal);
        return bal;
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      console.error("Failed to fetch wallet balance", error);
      setWalletBalance(0);
      return 0;
    } finally {
      setLoadingBalance(false);
    }
  };

  const queryRemitterHandler = async () => {
    if (!mobile.trim() || mobile.length !== 10) {
      Swal.fire(
        "Oops!",
        "Please enter a valid 10-digit mobile number",
        "warning",
      );
      return;
    }

    if (premiumStatus !== "true") {
      setShowDialog(true);
      return;
    }

    // Block if not onboarded
    if (!isOnboarded) {
      setShowOnboardModal(true);
      return;
    }

    if (loading) return;
    setLoading(true);

    try {
      const result = await FinoDmtService.queryRemitter(mobile);
      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;
      if (res.status === "SUCCESS") {
        if (res.message === "REMITTER_ACCOUNT_FOUND") {
          navigate("/fino-money-transfer/sender-home", {
            state: {
              customerMobile: mobile,
              data: res?.data,
            },
          });
        } else if (res.message === "REMITTER_ACCOUNT_NOT_FOUND") {
          const url = res?.data?.url;
          if (url) {
            setAccountOpeningUrl(url);
            const currentBalance = await fetchWalletBalance();
            setWalletBalance(currentBalance);
            setShowAccountModal(true);
          } else {
            Swal.fire("Oops!", "Account opening link not found", "error");
          }
        }
      } else {
        // ACCOUNT REVISION
        if (res.status == "ERROR" && res.message == "ACCOUNT_REVISION") {
          const url = res?.data?.url;
          const response = res?.data?.response; // reason for account form revision
          if (url) {
            setRevisionResponse(response);
            setAccountOpeningUrl(url);
            setShowAccountRevisionModal(true);
          } else {
            Swal.fire("Oops!", "Account opening link not found", "error");
          }
        } else {
          Swal.fire("Oops!", res.message || "Something went wrong", "error");
        }
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAccount = () => {
    if (accountOpeningUrl) {
      window.open(accountOpeningUrl, "_blank");
      setShowAccountModal(false);
      setShowAccountRevisionModal(false);
    }
  };

  const handleGoToOnboarding = () => {
    setShowOnboardModal(false);
    navigate("/bc-agent-onboarding");
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />

        <Col md={9}>
          <Card className="shadow-sm p-4 mb-4" style={{ borderRadius: "12px" }}>
            {/* Header with Powered by Fino Bank Logo */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center">
                <div className="bg-primary p-2 rounded-circle me-3">
                  <CreditCard size={24} color="white" />
                </div>
                <div>
                  <h4 className="mb-1">Fino Money Transfer</h4>
                  <p className="text-muted mb-0">
                    Instant money transfer by Fino Payments Bank
                  </p>
                </div>
              </div>
              <div className="text-end">
                <img
                  src="/assets/images/fino-bank-logo.png"
                  alt="fino-logo"
                  height={20}
                />
              </div>
            </div>

            <Alert variant="success" className="d-flex align-items-center mb-4">
              <div className="small">
                <strong>Transfer up to ₹3,00,000 (3 Lakh) per customer</strong>{" "}
                – Send money to any bank account instantly.
              </div>
            </Alert>

            <Row className="g-4">
              <Col md={6}>
                <Card className="border h-100">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-light p-2 rounded-circle me-3">
                        <Phone size={20} className="text-primary" />
                      </div>
                      <h5 className="mb-0">Customer Mobile Number</h5>
                    </div>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">
                        <Smartphone size={18} className="me-2 text-primary" />
                        Enter customer mobile number
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="bg-light">
                          +91
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Enter 10-digit mobile number"
                          value={mobile}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            if (value.length <= 10) {
                              setMobile(value);
                            }
                          }}
                          maxLength="10"
                          disabled={loading}
                          className="py-3"
                        />
                      </InputGroup>
                    </Form.Group>

                    <Button
                      variant="primary"
                      size="lg"
                      onClick={queryRemitterHandler}
                      disabled={loading || !mobile || mobile.length !== 10}
                      className="w-100 py-3"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Send size={20} className="me-2" />
                          Search
                        </>
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="border h-100">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-light p-2 rounded-circle me-3">
                        <Shield size={20} className="text-primary" />
                      </div>
                      <h5 className="mb-0">Secure & Instant Transfer</h5>
                    </div>

                    <div className="mb-4">
                      <div className="d-flex align-items-start mb-3">
                        <CheckCircle
                          size={18}
                          className="text-success me-3 mt-1"
                        />
                        <div>
                          <h6 className="mb-1">
                            Instant Sender Account Opening
                          </h6>
                          <p className="text-muted small mb-0">
                            Open sender account instantly with Aadhaar e-KYC
                          </p>
                        </div>
                      </div>

                      <div className="d-flex align-items-start mb-3">
                        <CheckCircle
                          size={18}
                          className="text-success me-3 mt-1"
                        />
                        <div>
                          <h6 className="mb-1">Instant Transfer</h6>
                          <p className="text-muted small mb-0">
                            Money transfers happen in real-time
                          </p>
                        </div>
                      </div>

                      <div className="d-flex align-items-start mb-3">
                        <CheckCircle
                          size={18}
                          className="text-success me-3 mt-1"
                        />
                        <div>
                          <h6 className="mb-1">24/7 Service</h6>
                          <p className="text-muted small mb-0">
                            Available round the clock for transfers
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-top">
                      <h6 className="mb-3">Quick Actions</h6>
                      <div className="d-grid gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            navigate(
                              "/fino-money-transfer/money-transfer-history",
                            );
                          }}
                        >
                          <Eye size={16} className="me-2" />
                          View Money Transfer History
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            navigate("/fino-money-transfer/claim-refund");
                          }}
                        >
                          <RefreshCw size={16} className="me-2" />
                          Claim Refund
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="border mt-4">
              <Card.Header className="bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Transaction Information</h6>
                  <Badge bg="info" className="px-3 py-2">
                    <Shield size={14} className="me-1" /> Fully RBI Compliant
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <div className="text-center p-3">
                      <div className="bg-success bg-opacity-10 p-3 rounded-circle d-inline-flex mb-2">
                        <CreditCard size={24} className="text-info" />
                      </div>
                      <h6 className="mb-1">Fino Payments Bank</h6>
                      <p className="text-muted small mb-0">RBI Licensed</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center p-3">
                      <div className="bg-success bg-opacity-10 p-3 rounded-circle d-inline-flex mb-2">
                        <Send size={24} className="text-success" />
                      </div>
                      <h6 className="mb-1">Instant Transfer</h6>
                      <p className="text-muted small mb-0">
                        Real-time processing
                      </p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center p-3">
                      <div className="bg-info bg-opacity-10 p-3 rounded-circle d-inline-flex mb-2">
                        <Shield size={24} className="text-info" />
                      </div>
                      <h6 className="mb-1">Secure KYC</h6>
                      <p className="text-muted small mb-0">
                        Aadhaar Based e-KYC Verification
                      </p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Card>
        </Col>
      </Row>

      {/* Account Opening Modal */}
      <Modal
        show={showAccountModal}
        onHide={() => setShowAccountModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Zap size={20} className="me-2 text-warning" />
            Open Sender Account
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <strong>As per RBI Guidelines</strong> – Transfer money up to
            ₹3,00,000 per month per sender.
          </Alert>
          <div className="mb-4">
            <h6 className="fw-bold">📋 Account Opening Details</h6>
            <ul className="mt-2">
              <li>✅ Paperless Aadhaar e-KYC process</li>
              <li>✅ Aadhaar and PAN Card required</li>
              <li>✅ Instant account activation</li>
              <li>✅ Send money to any bank account</li>
            </ul>
          </div>
          <div className="mb-4 p-3 bg-light rounded">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted">Account opening charges:</span>
                <strong className="ms-2 text-danger">₹10</strong>
                <small className="text-muted d-block">
                  (Will be deducted from wallet)
                </small>
              </div>
              <div className="text-end">
                <span className="text-muted">Your wallet balance:</span>
                <strong
                  className={`ms-2 ${
                    walletBalance >= 10 ? "text-success" : "text-danger"
                  }`}
                >
                  ₹{walletBalance.toFixed(2)}
                </strong>
              </div>
            </div>
            {loadingBalance && (
              <small className="text-muted">Checking balance...</small>
            )}
          </div>
          {walletBalance < 10 && (
            <Alert variant="danger" className="py-2">
              <AlertCircle size={16} className="me-1" />
              Insufficient balance (₹10 required). Please recharge your wallet.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAccountModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleOpenAccount}
            disabled={loadingBalance || walletBalance < 10}
          >
            {loadingBalance ? "Checking..." : "Proceed to Open Account"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Account Revision Modal */}
      {/* Account Revision Modal */}
      <Modal
        show={showAccountRevisionModal}
        onHide={() => setShowAccountRevisionModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Zap size={20} className="me-2 text-warning" />
            Account Revision Required
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <strong>Action Required:</strong> Your sender account needs revision
            before you can proceed.
          </Alert>

          <div className="mb-4">
            <h6 className="fw-bold">🔧 What you need to do</h6>

            <ul>
              <li>
                <strong style={{ color: "red" }}>{revisionResponse}</strong>
              </li>
            </ul>
          </div>

          <div className="bg-light p-3 rounded">
            <small className="text-muted">
              This revision is required as per banking compliance guidelines. No
              charges will be applied for updating your account details.
            </small>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAccountRevisionModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleOpenAccount}>
            Update Account Details
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Onboarding Required Modal - Improved Design */}
      <Modal
        show={showOnboardModal}
        onHide={() => setShowOnboardModal(false)}
        centered
        backdrop="static"
        size="md"
      >
        <Modal.Header className="bg-danger text-white border-0">
          <Modal.Title className="d-flex align-items-center">
            <AlertCircle size={28} className="me-2" />
            Onboarding Required
          </Modal.Title>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={() => setShowOnboardModal(false)}
            aria-label="Close"
          />
        </Modal.Header>

        <Modal.Body className="pt-4">
          <div className="text-center mb-3">
            <div className="bg-warning bg-opacity-10 rounded-circle p-3 d-inline-flex mb-3">
              <Shield size={48} className="text-warning" />
            </div>
            <h5 className="mb-2">Access Restricted</h5>
            <p className="text-muted mb-3">
              You need to complete onboarding before using Fino Money Transfer.
            </p>
          </div>

          <Alert variant="danger" className="mb-3">
            <div className="d-flex">
              <AlertCircle size={18} className="me-2 flex-shrink-0" />
              <div>
                <strong>Missing Requirements:</strong>
                <ul className="mb-0 mt-1 ps-3">
                  {merchantStatus !== "APPROVED" && (
                    <li>Merchant onboarding not approved</li>
                  )}
                  {finoEkycStatus !== "VERIFIED" && (
                    <li>FINO e-KYC not verified</li>
                  )}
                </ul>
              </div>
            </div>
          </Alert>

          <div className="bg-light p-3 rounded">
            <div className="d-flex align-items-start">
              <Info size={18} className="text-primary me-2 mt-0.5" />
              <small className="text-muted">
                Once your onboarding is approved and e-KYC is verified, you'll
                be able to:
                <ul className="mt-1 mb-0">
                  <li>Send money up to ₹3,00,000 per month</li>
                  <li>Open instant sender accounts</li>
                  <li>Access 24/7 money transfer service</li>
                </ul>
              </small>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 justify-content-center gap-3">
          <Button
            variant="light"
            onClick={() => setShowOnboardModal(false)}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleGoToOnboarding}
            className="px-4"
          >
            Complete Onboarding
          </Button>
        </Modal.Footer>
      </Modal>

      <SubscriptionDialog
        show={showDialog}
        onHide={() => setShowDialog(false)}
        serviceName="Fino Money Transfer"
      />
    </Container>
  );
};

export default FinoMoneyTransfer;
